/**
 * BreadcrumbService
 * 
 * The core engine of the Breadcrumb System. It maintains an in-memory ring buffer 
 * limited to a fixed number of items (MAX_SIZE = 20). It provides the `add()` method for pushing 
 * new events and the `getSnapshot()` method for safely retrieving the chronological list of events.
 * 
 * This service is entirely client-side and memory-bound. Breadcrumbs live only for the 
 * lifetime of the browser tab and are attached to error payloads in `log-error.util.ts`.
 */
import { sanitizeMetadata } from './breadcrumb-sanitizer.util';

export type BreadcrumbCategory = 
  | 'navigation' 
  | 'auth' 
  | 'business_event';

export type BreadcrumbSeverity = 'info' | 'warning' | 'error';

export interface Breadcrumb {
  id: number;
  timestamp: string;
  category: BreadcrumbCategory;
  message: string;
  severity: BreadcrumbSeverity;
  metadata?: Record<string, unknown>;
}

export type BreadcrumbInput = Omit<Breadcrumb, 'id' | 'timestamp' | 'severity'> & {
  severity?: BreadcrumbSeverity;
};

const MAX_SIZE = 20;

export class BreadcrumbService {
  private static buffer: Breadcrumb[] = new Array(MAX_SIZE);
  private static head = 0;
  private static count = 0;
  private static sequenceCounter = 1;

  /**
   * Adds a new breadcrumb to the ring buffer.
   * If the buffer is full, the oldest breadcrumb is overwritten.
   */
  public static add(payload: BreadcrumbInput): void {
    const timestamp = new Date().toISOString();
    
    const breadcrumb: Breadcrumb = {
      id: this.sequenceCounter++,
      timestamp,
      category: payload.category,
      message: payload.message,
      severity: payload.severity || 'info',
      metadata: sanitizeMetadata(payload.metadata)
    };

    this.buffer[this.head] = breadcrumb;
    this.head = (this.head + 1) % MAX_SIZE;
    this.count = Math.min(this.count + 1, MAX_SIZE);
  }

  /**
   * Retrieves an immutable, chronologically ordered snapshot of all breadcrumbs.
   */
  public static getSnapshot(): Breadcrumb[] {
    if (this.count === 0) {
      return [];
    }

    let snapshot: Breadcrumb[];

    if (this.count < MAX_SIZE) {
      // Buffer not full, elements are from index 0 to count-1
      snapshot = this.buffer.slice(0, this.count);
    } else {
      // Buffer is full, ordered from head to end, then 0 to head-1
      const oldest = this.buffer.slice(this.head);
      const newest = this.buffer.slice(0, this.head);
      snapshot = oldest.concat(newest);
    }

    // Return a shallow clone of the breadcrumbs to prevent mutation.
    // The metadata is already a fresh object from sanitizeMetadata.
    return snapshot.map(b => ({ ...b }));
  }

  /**
   * Clears the breadcrumb buffer.
   * This is primarily intended for unit tests and local development.
   */
  public static clear(): void {
    this.buffer = new Array(MAX_SIZE);
    this.head = 0;
    this.count = 0;
    // We intentionally do not reset sequenceCounter so IDs remain unique across clears
  }
}
